import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@stores/ThemeContext";

import CustomPopup from "./PopUp";
import Loader from "@components/LoaderComponent/loader";
import Confirmationpopup from "@components/ConfirmationPopup";
import RegionItem from "./RegionItem";

import { SaveTenantHierarchy, GetSFMDisplay } from "@helpers/helpers";
import {
  handleDeleteClick,
  handleDeleteAction,
  handleAddClick,
  handleEditClick,
  filterHierarchy,
} from "./helpers";

import { FileText } from "lucide-react";

import SfmModalStore from "@stores/SfmModalStore";

import {
  FIRST_LEVEL,
  SECOND_LEVEL,
  THIRD_LEVEL,
  INITIAL_PAGE,
  SINGLE_VALUE,
  OUTLETS_PER_PAGE,
} from "./constants";

const transformData = (data, i18n) => {
  const regions = data.filter((item) => item.Level === FIRST_LEVEL);
  const outlets = data.filter((item) => item.Level === SECOND_LEVEL);
  const branchesAndTerminals = data.filter(
    (item) => item.Level === THIRD_LEVEL
  );

  return regions.map((region) => ({
    id: region.OrgId,
    name: region.OrgDetails.find(
      (detail) => detail.LanguageCode === i18n.language
    )?.ShortName,
    outlets: outlets
      .filter((outlet) => outlet.ParentOrgId === region.OrgId)
      .map((outlet) => {
        const branches = branchesAndTerminals.filter(
          (item) => item.ParentOrgId === outlet.OrgId
        );
        const branchData = branches.map((branch) => {
          const addresses =
            branch?.OrgAddresses?.map((address) => {
              const addressDetail = address.AddressDetails.find(
                (detail) => detail.LanguageCode === i18n.language
              );
              return `${addressDetail.District}, ${addressDetail.Sector}, ${addressDetail.City}, ${addressDetail.StreetAddress}`;
            }) || [];

          const contacts = branch?.OrgContacts?.map((contact) => ({
            ContactValue: contact.ContactValue,
            ContactTypeTag: contact.ContactTypeTag,
          }));

          const attributes = branch.OrgAttributes.map(
            (attribute) => attribute.AttributeValue
          ).join(" | ");

          return {
            id: branch.OrgId,
            name: `${
              branch.OrgDetails.find(
                (detail) => detail.LanguageCode === i18n.language
              ).ShortName
            } (${attributes})`,
            addresses,
            contacts,
            // terminals: devices,
            terminals: branch?.OrgDevices?.map((device) => ({
              id: device.DeviceId,
              name: device.DeviceName,
            })),
          };
        });

        return {
          id: outlet.OrgId,
          name: outlet.OrgDetails.find(
            (detail) => detail.LanguageCode === i18n.language
          ).ShortName,
          branches: branchData,
        };
      }),
  }));
};

const HierarchyComponent = ({
  data,
  setInfoPopupOpen,
  setCurrentBranch,
  searchTerm,
  setSearchTerm,
  refetchData,
}) => {
  const { t: translate, i18n } = useTranslation();

  const [showAllTerminals, setShowAllTerminals] = useState({});
  const [expandedOutlets, setExpandedOutlets] = useState({});
  const [expandedBranches, setExpandedBranches] = useState({});
  const [currentPage, setCurrentPage] = useState(INITIAL_PAGE);
  const [originalData, setOriginalData] = useState(data);
  const [currentData, setCurrentData] = useState(data);
  const [isDataModified, setIsDataModified] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subMenuID, setSubMenuID] = useState(null);
  const [openConfirmationPopup, setOpenConfirmationPopup] = useState(false);
  const [currentType, setCurrentType] = useState(null);
  const [currentDeleteId, setCurrentDeleteId] = useState(null);
  const { isDarkMode, toggleTheme } = useTheme();

  const hierarchy = transformData(currentData, i18n);

  const [expandedRegions, setExpandedRegions] = useState(
    !!hierarchy[FIRST_LEVEL]?.id
      ? {
          [hierarchy[FIRST_LEVEL]?.id]: true,
        }
      : {}
  );

  const handleApiCall = (updatedData) => {
    setLoading(true);
    SaveTenantHierarchy(updatedData, (data) => {
      setLoading(false);
      setSelectedData(null);
      setCurrentData(updatedData);
      setIsDataModified(true);
      setIsPopupOpen(false);
      const sfmData = GetSFMDisplay(data.SFM);
      SfmModalStore.open(sfmData, () => {
        refetchData();
      });
    });
  };

  const handleSaveChanges = (updatedChanges, isNew = false) => {
    const updatedData = isNew
      ? [...currentData, updatedChanges]
      : currentData.map((item) =>
          item.OrgId === updatedChanges.OrgId ? updatedChanges : item
        );
    handleApiCall(updatedData);
  };

  useEffect(() => {
    setCurrentPage(INITIAL_PAGE);

    if (searchTerm === "") {
      setExpandedOutlets({});
      setExpandedBranches({});
    }
  }, [searchTerm]);

  const filteredHierarchy = useMemo(() => {
    if (searchTerm === "") {
      return hierarchy;
    }

    const result = filterHierarchy({
      hierarchy,
      searchTerm,
      setExpandedRegions,
      setExpandedOutlets,
      setExpandedBranches,
    });
    return result;
  }, [searchTerm]);

  const currentOutlets = filteredHierarchy[FIRST_LEVEL]?.outlets.slice(
    (currentPage - INITIAL_PAGE) * OUTLETS_PER_PAGE,
    currentPage * OUTLETS_PER_PAGE
  );

  const handleToggleShowAll = (branchId) => {
    setShowAllTerminals((prev) => ({
      ...prev,
      [branchId]: !prev[branchId],
    }));
  };

  // Build action menu for outlets
  const getOutletActions = (region) => [
    {
      name: translate("hierarchy.actions.add-outlet"),
      value: "add",
      action: (id, type, parent) =>
        handleAddClick({
          sentData: id,
          type: "outlet",
          parent: parent || region,
          isTerminal: false,
          currentData,
          setSubMenuID,
          setSelectedData,
          setIsPopupOpen,
          originalData,
        }),
    },
    {
      name: translate("hierarchy.actions.edit-area"),
      value: "edit",
      action: (id, type, parent) =>
        handleEditClick({
          sentData: id,
          type: "outlet",
          parent: parent || region,
          isTerminal: false,
          addType: "outlet",
          currentData,
          setSubMenuID,
          setSelectedData,
          setIsPopupOpen,
        }),
    },
    {
      name: translate("hierarchy.actions.delete-area"),
      value: "delete",
      action: () =>
        handleDeleteClick({ setCurrentType, setOpenConfirmationPopup, type: "area" }),
      color: "red-500",
    },
  ];

  // Build action menu for branches
  const getBranchActions = () => [
    {
      name: translate("hierarchy.actions.add-terminal"),
      value: "add",
      action: (id, type, parent) =>
        handleAddClick({
          sentData: id,
          type: "terminal",
          parent: parent,
          isTerminal: false,
          currentData,
          setSubMenuID,
          setSelectedData,
          setIsPopupOpen,
          originalData,
        }),
    },
    {
      name: translate("hierarchy.actions.edit-outlet"),
      value: "edit",
      action: (id, type, parent) =>
        handleEditClick({
          sentData: id,
          type: "branch",
          parent: parent,
          isTerminal: false,
          addType: "terminal",
          currentData,
          setSubMenuID,
          setSelectedData,
          setIsPopupOpen,
        }),
    },
    {
      name: translate("hierarchy.actions.delete-outlet"),
      value: "delete",
      action: () =>
        handleDeleteClick({ setCurrentType, setOpenConfirmationPopup, type: "outlet" }),
      color: "red-500",
    },
  ];

  // Build action menu for terminals
  const getTerminalActions = () => [
    {
      name: translate("hierarchy.actions.edit-terminal"),
      value: "edit",
      action: (id, type, parent, sentTerminalId) =>
        handleEditClick({
          sentData: id,
          type: "terminal",
          parent: parent,
          isTerminal: true,
          sentTerminalId: sentTerminalId,
          addType: "terminal",
          currentData,
          setSubMenuID,
          setSelectedData,
          setIsPopupOpen,
        }),
    },
    {
      name: translate("hierarchy.actions.delete-terminal"),
      value: "delete",
      action: (id, type, parent, sentTerminalId) => {
        setCurrentDeleteId(sentTerminalId);
        handleDeleteClick({ setCurrentType, setOpenConfirmationPopup, type: "terminal" });
      },
      color: "red-500",
    },
  ];

  return loading ? (
    <Loader dots />
  ) : (
    <>
      <div>
        <input
          type="text"
          placeholder={translate("shared.search")}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          className="mb-4 p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        />
        {!!filteredHierarchy.length ? (
          filteredHierarchy.map((region) => (
            <RegionItem
              key={region.id}
              region={region}
              expandedRegions={expandedRegions}
              expandedOutlets={expandedOutlets}
              expandedBranches={expandedBranches}
              searchTerm={searchTerm}
              subMenuID={subMenuID}
              setSubMenuID={setSubMenuID}
              currentOutlets={currentOutlets}
              showAllTerminals={showAllTerminals}
              handleToggleShowAll={handleToggleShowAll}
              setCurrentBranch={setCurrentBranch}
              setInfoPopupOpen={setInfoPopupOpen}
              setExpandedRegions={setExpandedRegions}
              setExpandedOutlets={setExpandedOutlets}
              setExpandedBranches={setExpandedBranches}
              outletActions={getOutletActions(region)}
              branchActions={getBranchActions()}
              terminalActions={getTerminalActions()}
            />
          ))
        ) : (
          <div className="text-center w-full flex flex-col dark:text-white items-center gap-4">
            <FileText size={48} />
            <span>
              {translate("hierarchy.no-results")} {searchTerm}
            </span>
          </div>
        )}

        {openConfirmationPopup && (
          <Confirmationpopup
            singleAction={{
              actionFN: () =>
                handleDeleteAction({
                  id: currentDeleteId || subMenuID,
                  isTerminal: currentType === "terminal" ? true : false,
                  currentData,
                  handleApiCall,
                }),
              confirmation: true,
              confirmationMessage: `You are about to delete ${currentType} id: ${currentDeleteId || subMenuID}. Are you sure?`,
              severity: "high",
            }}
            setOpenConfirmationAction={setOpenConfirmationPopup}
          />
        )}
      </div>
      {isPopupOpen && selectedData && (
        <CustomPopup
          selectedData={selectedData}
          onSave={handleSaveChanges}
          setPopupOpen={(value) => setIsPopupOpen(value)}
          isNew={
            selectedData.action === "add" && selectedData.type !== "terminal"
          }
        />
      )}
    </>
  );
};

export default HierarchyComponent;
